import { useState } from "react";
import { contactInfo } from "../data/mock";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    nom_prenom: "",
    telephone: "",
    email: "",
    demande: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const [ref, isVisible] = useScrollAnimation(0.1);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUsedFallback(false);

    try {
      // Save to backend + trigger the owner email notification
      await axios.post(`${API}/contact`, formData, { timeout: 15000 });
    } catch (err) {
      // Backend unreachable: fallback to the visitor's mail client (kept as backup)
      setUsedFallback(true);
      const subject = encodeURIComponent(`Demande Van d'Iroise - ${formData.nom_prenom}`);
      const body = encodeURIComponent(
        `Nom et Prénom : ${formData.nom_prenom}\nTéléphone : ${formData.telephone}\nEmail : ${formData.email}\n\nDemande :\n${formData.demande}`
      );
      window.open(
        `mailto:${contactInfo.email}?subject=${subject}&body=${body}`,
        "_blank"
      );
    }

    setLoading(false);
    setSubmitted(true);
  };

  return (
    <section ref={ref} id="contact" data-testid="contact-section" className="py-[100px] md:py-[120px] bg-[#f8f7f2]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Left - Info */}
          <div>
            <p className="text-[12px] tracking-[0.2em] uppercase text-[#c9a84c] mb-4">
              Contact
            </p>
            <h2 className="text-[30px] md:text-[36px] font-normal text-[#1a1d2e] leading-[1.2] mb-4">
              Prenons contact
            </h2>
            <div className="gold-line mb-6" />
            <p className="text-[16px] text-[#555] leading-[1.7] mb-10 max-w-[460px]">
              Pour une réservation ou un devis personnalisé, n'hésitez pas à nous contacter par téléphone, par email ou via le formulaire ci-contre.
            </p>

            {/* Contact Items */}
            <div className="space-y-6">
              {/* Phone */}
              <a
                data-testid="contact-phone-link"
                href={contactInfo.phoneLink}
                className="flex items-center gap-5 group"
              >
                <div className="w-[52px] h-[52px] flex items-center justify-center border border-[#c9a84c]/30 transition-all duration-200 group-hover:border-[#c9a84c] group-hover:bg-[#c9a84c]">
                  <Phone
                    size={20}
                    strokeWidth={1.5}
                    className="text-[#c9a84c] transition-colors duration-200 group-hover:text-[#1a1d2e]"
                  />
                </div>
                <div>
                  <p className="text-[12px] text-[#1a1d2e]/40 mb-1">
                    Téléphone
                  </p>
                  <p className="text-[18px] text-[#1a1d2e] font-normal group-hover:text-[#c9a84c] transition-colors">
                    {contactInfo.phone}
                  </p>
                </div>
              </a>

              {/* Email */}
              <a
                data-testid="contact-email-link"
                href={contactInfo.emailLink}
                className="flex items-center gap-5 group"
              >
                <div className="w-[52px] h-[52px] flex items-center justify-center border border-[#c9a84c]/30 transition-all duration-200 group-hover:border-[#c9a84c] group-hover:bg-[#c9a84c]">
                  <Mail
                    size={20}
                    strokeWidth={1.5}
                    className="text-[#c9a84c] transition-colors duration-200 group-hover:text-[#1a1d2e]"
                  />
                </div>
                <div>
                  <p className="text-[12px] text-[#1a1d2e]/40 mb-1">
                    Email
                  </p>
                  <p className="text-[16px] text-[#1a1d2e] font-normal group-hover:text-[#c9a84c] transition-colors break-all">
                    {contactInfo.email}
                  </p>
                </div>
              </a>

              {/* Location */}
              <div className="flex items-center gap-5">
                <div className="w-[52px] h-[52px] flex items-center justify-center border border-[#c9a84c]/30">
                  <MapPin size={20} strokeWidth={1.5} className="text-[#c9a84c]" />
                </div>
                <div>
                  <p className="text-[12px] text-[#1a1d2e]/40 mb-1">
                    Zone d'intervention
                  </p>
                  <p className="text-[16px] text-[#1a1d2e] font-normal">
                    {contactInfo.location}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="flex items-start">
            <div className="bg-[#1a1d2e] p-8 md:p-12 w-full">
              {submitted ? (
                /* Success Message */
                <div data-testid="contact-success-message" className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircle size={48} strokeWidth={1.5} className="text-[#c9a84c] mb-6" />
                  <h3 className="text-[22px] font-normal text-white mb-4">
                    Demande envoyée
                  </h3>
                  <p className="text-[16px] text-white/60 leading-[1.7] max-w-[360px]">
                    {usedFallback
                      ? "Votre messagerie s'est ouverte pour finaliser l'envoi de votre demande."
                      : "Nous reviendrons vers vous dans les meilleurs délais !"}
                  </p>
                  <button
                    data-testid="contact-send-another-button"
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ nom_prenom: "", telephone: "", email: "", demande: "" });
                    }}
                    className="mt-8 text-[14px] text-[#c9a84c] hover:text-[#d4af5c] transition-colors"
                  >
                    Envoyer une autre demande
                  </button>
                </div>
              ) : (
                /* Form */
                <>
                  <h3 className="text-[22px] md:text-[26px] font-normal text-white leading-[1.3] mb-2">
                    Demandez votre devis
                  </h3>
                  <div className="gold-line mb-8" />

                  <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5">
                    {/* Nom et Prénom */}
                    <div>
                      <label className="block text-[12px] tracking-[0.1em] uppercase text-white/40 mb-2">
                        Nom et Prénom
                      </label>
                      <input
                        data-testid="contact-input-nom"
                        type="text"
                        name="nom_prenom"
                        required
                        value={formData.nom_prenom}
                        onChange={handleChange}
                        className="w-full bg-transparent border border-white/15 text-white text-[14px] px-4 py-3.5 focus:border-[#c9a84c] focus:outline-none transition-colors placeholder:text-white/20"
                        placeholder="Votre nom complet"
                      />
                    </div>

                    {/* Téléphone */}
                    <div>
                      <label className="block text-[12px] tracking-[0.1em] uppercase text-white/40 mb-2">
                        Téléphone
                      </label>
                      <input
                        data-testid="contact-input-telephone"
                        type="tel"
                        name="telephone"
                        required
                        value={formData.telephone}
                        onChange={handleChange}
                        className="w-full bg-transparent border border-white/15 text-white text-[14px] px-4 py-3.5 focus:border-[#c9a84c] focus:outline-none transition-colors placeholder:text-white/20"
                        placeholder="06 00 00 00 00"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-[12px] tracking-[0.1em] uppercase text-white/40 mb-2">
                        Email
                      </label>
                      <input
                        data-testid="contact-input-email"
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-transparent border border-white/15 text-white text-[14px] px-4 py-3.5 focus:border-[#c9a84c] focus:outline-none transition-colors placeholder:text-white/20"
                        placeholder="votre@email.com"
                      />
                    </div>

                    {/* Demande */}
                    <div>
                      <label className="block text-[12px] tracking-[0.1em] uppercase text-white/40 mb-2">
                        Demande
                      </label>
                      <textarea
                        data-testid="contact-input-demande"
                        name="demande"
                        required
                        value={formData.demande}
                        onChange={handleChange}
                        rows={5}
                        className="w-full bg-transparent border border-white/15 text-white text-[14px] px-4 py-3.5 focus:border-[#c9a84c] focus:outline-none transition-colors placeholder:text-white/25 placeholder:italic resize-none"
                        placeholder="Adresses de départ et de destination, date et heure de prise en charge, numéro de vol ..."
                      />
                    </div>

                    {/* Submit */}
                    <button
                      data-testid="contact-submit-button"
                      type="submit"
                      disabled={loading}
                      className="flex items-center justify-center gap-2 w-full py-4 border border-[#c9a84c] text-[#c9a84c] text-[14px] font-medium hover:bg-[#c9a84c] hover:text-[#1a1d2e] transition-all duration-200 disabled:opacity-50"
                    >
                      {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                      <Send size={16} />
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
